import { useEffect, useState } from 'react';
import type { ApiClient, Parameterfuellgrad as Zahlen, Gewerkfuellgrad } from '../api';
import { Farbregellegende, Feldfarbe } from '../Farbregel';
import {
  ContentSection, DataTable, EmptyState, ErrorState, LoadingState, type Spalte,
} from '../muster';

type Feldzeile = NonNullable<Gewerkfuellgrad['felder']>[number];

/**
 * Der Parameterfüllgrad als Karte (Vertiefungsplan V-P1).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * JEDE ZAHL MIT IHREM NENNER — UND JEDER NENNER MIT SEINER REGEL
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Der Nenner ist NICHT „Bauteile mal Felder". Er war es bis zum 20.09.2026,
 * und der Kopf dieser Datei behauptete es bis PB30 weiter, obwohl fünf der
 * acht Kataloge längst eine Anwendbarkeitsliste führen — die Seite beschrieb
 * eine Rechnung, die der Server nicht mehr machte.
 *
 * Heute zählt jedes Feld gegen die Objekte, an denen es fachlich GILT. Zwei
 * Gewerke zählen dabei gegen MEHR ALS EIN Objekt:
 *
 *   Lüftung    99 Felder je Bauteil (7.081), 141 je Anlage (108),
 *              128 je Einheit (121), 32 ohne jede Ablage
 *   Sanitär    23 Felder je Rohrzeile (41.335), 51 je Gerät (317),
 *              4 je Gegenstand (2.938)
 *
 * Die Spalte „Bauteile" zeigt deshalb NICHT eine Zahl, sondern die Nenner
 * einzeln. Vier Nenner unter einer Überschrift sind schlimmer als einer: Wer
 * neben „400 Felder" die Zahl 7.081 liest, rechnet 2,8 Millionen mögliche
 * Werte — es sind 497.411.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DREI ARTEN VON NULL
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   leere Felder        Nenner > 0, kein Wert   → Arbeitsauftrag
 *   ohne Bauteilklasse  Nenner 0, weil die Art in der Tabelle fehlt
 *   ohne Ablage         Nenner 0, weil es die Tabelle nicht gibt
 *
 * Die dritte Zahl liefert der Server seit jeher und die Seite zeigte sie
 * nicht. Ohne sie ging der Bericht der Lüftung nicht auf: 400 Felder, davon
 * 109 ohne Bauteilklasse und 32 ohne Ablage.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM DIE FELDER ERST AUF KLICK KOMMEN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Über alle Gewerke sind es weit über neunhundert Felder, und jedes trägt
 * seine Herkunftsverteilung. Bei jedem Seitenaufruf wäre das eine
 * Auszählung über den halben Bestand — für eine Liste, die die meisten nie
 * aufklappen.
 */
export function Parameterfuellgrad({
  api, projektId,
}: {
  api: ApiClient;
  projektId: string;
}) {
  const [zahlen, setZahlen] = useState<Zahlen | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [offen, setOffen] = useState<string | null>(null);
  const [felder, setFelder] = useState<Record<string, Gewerkfuellgrad>>({});

  useEffect(() => {
    api.parameterFuellgrad(projektId)
      .then(setZahlen)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projektId]);

  function aufklappen(gewerk: string) {
    if (offen === gewerk) { setOffen(null); return; }
    setOffen(gewerk);

    if (felder[gewerk]) return;

    api.parameterFuellgrad(projektId, gewerk, true)
      .then((b) => {
        const g = b.gewerke.find((x) => x.gewerk === gewerk);
        if (g) setFelder((v) => ({ ...v, [gewerk]: g }));
      })
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }

  const laden = () => {
    setFehler(null);
    setZahlen(null);
    api.parameterFuellgrad(projektId)
      .then(setZahlen)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  };

  return (
    <ContentSection
      titel="Parameterfüllgrad der Gewerkekataloge"
      beschreibung={'Jedes Feld zählt gegen die Objekte, an denen es fachlich gilt — '
        + 'nicht gegen alle Bauteile des Gewerks. Lüftung und Sanitär haben dabei mehr '
        + 'als einen Nenner; welcher für welches Feld gilt, steht in der Spalte „gilt für".'}
      aktionen={(
        <button type="button" className="m-knopf" disabled={!zahlen}
          onClick={() => api.parameterFuellgradPdf(projektId)}>
          Füllgradbericht als PDF
        </button>
      )}
    >
      {fehler && (
        <ErrorState
          meldung="Der Parameterfüllgrad konnte nicht ermittelt werden."
          technisch={fehler}
          beiErneutVersuchen={laden}
        />
      )}

      {!zahlen && !fehler ? (
        <LoadingState text="Füllgrad wird ausgezählt …" zeilen={7} spalten={5} />
      ) : !zahlen ? null : (
        <>
          <div style={{ margin: '10px 0' }}>
            <Farbregellegende />
          </div>

          <table style={{ marginTop: 6 }}>
            <thead>
              <tr>
                <th>Gewerk</th>
                <th>Katalog</th>
                <th title="Wogegen gezählt wird. Mehr als eine Zeile heißt: mehr als ein Nenner.">
                  gezählt gegen
                </th>
                <th style={{ textAlign: 'right' }}>Felder</th>
                <th style={{ textAlign: 'right' }}>grün</th>
                <th style={{ textAlign: 'right' }}>gelb</th>
                <th style={{ textAlign: 'right' }}>leere Felder</th>
                <th style={{ textAlign: 'right' }}
                  title="Nenner 0, weil die Bauteilart in der Tabelle nicht vorkommt. Nicht leer — nicht anwendbar.">
                  o. Klasse
                </th>
                <th style={{ textAlign: 'right' }}
                  title="Nenner 0, weil es für diese Felder im Bestand gar keine Ablage gibt — nicht die Bauteilart fehlt, sondern die Tabelle.">
                  o. Ablage
                </th>
                <th style={{ textAlign: 'right' }}>möglich</th>
                <th style={{ textAlign: 'right' }}>gefüllt</th>
                <th style={{ textAlign: 'right' }}>Anteil</th>
              </tr>
            </thead>
            <tbody>
              {zahlen.gewerke.map((g) => (
                <GewerkZeile
                  key={g.gewerk}
                  gewerk={g}
                  offen={offen === g.gewerk}
                  felder={felder[g.gewerk]}
                  aufklappen={() => aufklappen(g.gewerk)}
                />
              ))}
              <tr style={{ fontWeight: 600 }}>
                <td colSpan={5}>Summe</td>
                <td style={{ textAlign: 'right' }}>—</td>
                <td style={{ textAlign: 'right' }}>{zahlen.felderOhneBauteilklasse}</td>
                <td style={{ textAlign: 'right' }}>{zahlen.felderOhneAblage}</td>
                <td style={{ textAlign: 'right' }}>{zahlen.moeglich.toLocaleString('de-DE')}</td>
                <td style={{ textAlign: 'right' }}>{zahlen.gefuellt.toLocaleString('de-DE')}</td>
                <td style={{ textAlign: 'right' }}>{prozent(zahlen.anteil)}</td>
              </tr>
            </tbody>
          </table>

          {zahlen.gewerke.length === 0 && (
            <EmptyState
              titel="Für dieses Projekt ist kein Gewerkekatalog gefüllt."
              erklaerung={'Der Füllgrad entsteht aus den aufgenommenen Bauteilen — ohne '
                + 'Aufnahme gibt es weder Zähler noch Nenner.'}
            />
          )}

          <div className="hinweis" style={{ marginTop: 12, fontSize: 11 }}>
            {zahlen.hinweise.map((h, i) => <p key={i} style={{ margin: '4px 0' }}>{h}</p>)}
          </div>
        </>
      )}
    </ContentSection>
  );
}

function GewerkZeile({
  gewerk, offen, felder, aufklappen,
}: {
  gewerk: Gewerkfuellgrad;
  offen: boolean;
  felder?: Gewerkfuellgrad;
  aufklappen: () => void;
}) {
  return (
    <>
      <tr
        onClick={aufklappen}
        style={{ cursor: 'pointer' }}
        title="Felder dieses Gewerks anzeigen"
      >
        <td>{offen ? '▾ ' : '▸ '}{gewerk.bezeichnung}</td>
        <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{gewerk.katalogtabelle}</td>
        <td style={{ fontSize: 11 }}><Nenner gewerk={gewerk} /></td>
        <td style={{ textAlign: 'right' }}>{gewerk.felderGesamt}</td>
        <td style={{ textAlign: 'right' }}>{gewerk.felderGruen}</td>
        <td style={{ textAlign: 'right' }}>{gewerk.felderGelb}</td>
        <td style={{ textAlign: 'right' }}>
          {/* Ein Feld ohne Nenner ist nicht leer, sondern nicht anwendbar. */}
          {(felder
            ? felder.felder?.filter((f) => f.gefuellt === 0 && f.gesamt > 0).length
            : gewerk.felderLeer) ?? '—'}
        </td>
        <td style={{ textAlign: 'right' }}>{gewerk.felderOhneBauteilklasse || '—'}</td>
        <td style={{ textAlign: 'right' }}>{gewerk.felderOhneAblage || '—'}</td>
        <td style={{ textAlign: 'right' }}>{gewerk.moeglich.toLocaleString('de-DE')}</td>
        <td style={{ textAlign: 'right' }}>{gewerk.gefuellt.toLocaleString('de-DE')}</td>
        <td style={{ textAlign: 'right' }}>{prozent(gewerk.anteil)}</td>
      </tr>

      {offen && (
        <tr>
          <td colSpan={12} style={{ padding: 0 }}>
            {!felder?.felder ? (
              <LoadingState text="Felder dieses Gewerks werden ausgezählt …" zeilen={6}
                spalten={4} />
            ) : (
              <DataTable
                name={`Felder ${gewerk.bezeichnung}`}
                spalten={FELD_SPALTEN}
                zeilen={felder.felder}
                schluessel={(f) => f.feldschluessel}
                dicht
                ausfuhr
                ausfuhrDateiname={`fuellgrad-${gewerk.gewerk}`}
                leer={(
                  <EmptyState
                    klein
                    titel="Dieser Katalog führt kein Feld."
                    erklaerung="Ohne Feld gibt es auch keinen Füllgrad."
                  />
                )}
              />
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Die Nenner eines Gewerks — einer je Zeile, der größte zuerst.
 *
 * Eine einzige Zahl stünde hier für die sechs Gewerke, die nur einen Nenner
 * haben, und wäre für die Lüftung und den Sanitär falsch: Dort zählen die
 * Felder gegen vier bzw. drei verschiedene Objekte, und die Summe „möglich"
 * daneben entsteht aus allen. Wer eine Bauteilzahl neben einer Feldzahl
 * sieht, multipliziert sie — genau das ergäbe für die Lüftung 2,8 Millionen
 * statt 497.411.
 */
function Nenner({ gewerk }: { gewerk: Gewerkfuellgrad }) {
  const nenner = gewerk.nenner ?? [];

  if (nenner.length === 0) {
    return <>{gewerk.bauteile.toLocaleString('de-DE')}</>;
  }

  return (
    <>
      {nenner.map((n) => (
        <div key={n.bezug} style={{ whiteSpace: 'nowrap' }}>
          <strong>{n.bezugsgroesse.toLocaleString('de-DE')}</strong>
          {' '}
          {n.bezug.replace(/^je /, '')}
          {' · '}
          {n.felder}
          {' Feld'}
          {n.felder === 1 ? '' : 'er'}
          {n.moeglich > 0 && (
            <>
              {' · '}
              {n.gefuellt.toLocaleString('de-DE')}
              {' / '}
              {n.moeglich.toLocaleString('de-DE')}
            </>
          )}
        </div>
      ))}
    </>
  );
}

/**
 * Die Herkunftswörter, die häufigsten zuerst.
 *
 * Eine Verteilung und keine einzelne Angabe: Ein Feld, das zu 40 % aus dem
 * Modell und zu 60 % aus einer Rechnung stammt, ist etwas anderes als eines,
 * das ganz aus einer Rechnung stammt — und genau das will man vor dem
 * Rückschreiben wissen.
 */
const FELD_SPALTEN: Spalte<Feldzeile>[] = [
  { schluessel: 'gruppe', titel: 'Gruppe', wert: (f) => f.gruppe ?? null },
  {
    schluessel: 'feldschluessel', titel: 'Feld', immerSichtbar: true,
    wert: (f) => f.feldschluessel,
    inhalt: (f) => (
      <Feldfarbe gehtInsModell={f.farbe === 'gruen'} kompakt>{f.feldschluessel}</Feldfarbe>
    ),
  },
  { schluessel: 'bezeichnung', titel: 'Bezeichnung', wert: (f) => f.bezeichnung },
  {
    schluessel: 'anwendbarkeit', titel: 'gilt für',
    wert: (f) => f.anwendbarkeit,
  },
  { schluessel: 'gefuellt', titel: 'gefüllt', art: 'zahl', wert: (f) => f.gefuellt },
  { schluessel: 'gesamt', titel: 'von', art: 'zahl', wert: (f) => f.gesamt },
  {
    schluessel: 'anteil', titel: 'Anteil', ausrichtung: 'rechts', breite: '100px',
    wert: (f) => f.anteil, inhalt: (f) => prozent(f.anteil),
  },
  {
    schluessel: 'ausserhalbAnwendbarkeit', titel: 'außerhalb', art: 'zahl',
    wert: (f) => f.ausserhalbAnwendbarkeit,
  },
  {
    schluessel: 'herkunft', titel: 'Herkunft der Werte', sortierbar: false,
    wert: (f) => herkunftstext(f.herkunft, f.offenGrund),
  },
];

function herkunftstext(herkunft: Record<string, number>, offenGrund: string | null): string {
  const teile = Object.entries(herkunft)
    .sort((a, b) => b[1] - a[1])
    .map(([wort, zahl]) => `${wort} ${zahl.toLocaleString('de-DE')}`);

  return teile.length === 0 ? (offenGrund ?? '—') : teile.join(' · ');
}

function prozent(anteil: number): string {
  return `${(anteil * 100).toLocaleString('de-DE', {
    minimumFractionDigits: 1, maximumFractionDigits: 1,
  })} %`;
}
